import { useContext, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import LineMinusCircleIcon from 'LuxKit/Icons/line/LineMinusCircleIcon'
import LinePlusCircleIcon from 'LuxKit/Icons/line/LinePlusCircleIcon'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import { trackEvent } from 'analytics/analytics'
import { propertyRateImpressionEventWithContext } from 'analytics/snowplow/events'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import RateImpressionContext from 'contexts/RateImpressionContext'

import {
  canShowCollapsedLeadPrice,
  getCheapestDisplayPriceRate,
  hasLuxPlusDisplayPrice,
} from './propertyCollapsedRoomPackageUtils'
import { getPropertyRateImpressionKey } from './propertyRateImpressionUtils'

const ActionsContainer = styled(Pane)`
  margin-top: ${rem(8)};
  overflow: hidden;
`

// Match the medium CTA height so toggling packages does not shift the room card height.
const LEAD_PRICE_BLOCK_STYLE = { height: rem(40) }

type PropertyRateImpressionContextData = Parameters<typeof propertyRateImpressionEventWithContext>[0]
type CollapsedPropertyRateImpressionContextData = Omit<
  PropertyRateImpressionContextData,
  'checkIn' | 'checkOut' | 'rate' | 'ratePosition'
> & {
  checkIn?: string
  checkOut?: string
}

export interface PropertyCollapsedRoomPackageTrackingData {
  contextData: CollapsedPropertyRateImpressionContextData
  originAirport?: App.Airport
}

interface Props {
  duration: number
  rates: Array<App.EnrichedPropertyRate>
  packageCount: number
  expanded: boolean
  trackingData: PropertyCollapsedRoomPackageTrackingData
  onTogglePackages: () => void
}

function PropertyCollapsedRoomPackageActions(props: Props) {
  const {
    duration,
    rates,
    packageCount,
    expanded,
    trackingData,
    onTogglePackages,
  } = props
  const {
    contextData,
    originAirport,
  } = trackingData

  const intl = useIntl()
  const impressionContext = useContext(RateImpressionContext)

  const leadRate = useMemo(() => getCheapestDisplayPriceRate(rates), [rates])
  const leadPriceRate = canShowCollapsedLeadPrice(leadRate) ? leadRate : undefined
  const hasLuxPlusLeadPrice = hasLuxPlusDisplayPrice(leadPriceRate)
  const showLeadPrice = !expanded && !!leadPriceRate
  const impressionKey = useMemo(() => {
    if (!leadPriceRate) return undefined
    return getPropertyRateImpressionKey(leadPriceRate, originAirport)
  }, [leadPriceRate, originAirport])
  const canFireImpression = !!(
    showLeadPrice &&
    leadPriceRate &&
    contextData.roomListId &&
    contextData.roomPosition &&
    contextData.rateListId &&
    impressionContext.isReady
  )
  const [leadPriceRef, leadPriceInView] = useInView({
    skip: !impressionKey || impressionContext.hasFired(impressionKey),
    threshold: 0.5,
  })

  const durationLabel = leadRate?.includesFlights ? intl.formatMessage(
    {
      id: 'MryM1bXgZ7gk864XJpkO',
      defaultMessage: '{duration, plural, one {# night} other {# nights}} + flights from',
      description: 'Stay duration and flights label shown above the collapsed property room lead package price.',
    },
    { duration },
  ) : intl.formatMessage(
    {
      id: '2XvptgjgCZfV2CrxKWNl',
      defaultMessage: '{duration, plural, one {# night} other {# nights}} from',
      description: 'Stay duration and from label shown above the collapsed property room lead package price.',
    },
    { duration },
  )

  useEffect(() => {
    const {
      checkIn,
      checkOut,
    } = contextData

    if (!canFireImpression || !leadPriceInView || !leadPriceRate || !impressionKey) return
    if (impressionContext.hasFired(impressionKey)) return

    trackEvent(propertyRateImpressionEventWithContext({
      ...contextData,
      checkIn,
      checkOut,
      ratePosition: leadPriceRate.defaultOrder + 1,
      rate: leadPriceRate,
    }))
    impressionContext.markAsFired(impressionKey)
  }, [
    canFireImpression,
    contextData,
    impressionContext,
    impressionKey,
    leadPriceInView,
    leadPriceRate,
  ])

  return <ActionsContainer padding="10 12" borderRadius="L">
    <Group
      direction={showLeadPrice ? 'horizontal' : 'vertical'}
      gap={showLeadPrice ? 24 : 8}
      verticalAlign="center"
      horizontalAlign="end"
    >
      {showLeadPrice && leadPriceRate && <Group
        ref={leadPriceRef}
        direction="vertical"
        gap={0}
        horizontalAlign="end"
        verticalAlign="center"
        style={LEAD_PRICE_BLOCK_STYLE}
      >
        <PriceRowPriceCaption>
          {durationLabel}
        </PriceRowPriceCaption>
        {hasLuxPlusLeadPrice && <PriceRowLuxPlusPrice
          data-testid="property-room-lead-luxplus-price"
          size="M"
          memberPrice={leadPriceRate.luxPlusTotals!.displayPrice}
          saleUnit="total"
        />}
        {!hasLuxPlusLeadPrice && <PriceRowPrice
          data-testid="property-room-lead-price"
          size="M"
          price={leadPriceRate.totals.displayPrice}
          saleUnit="total"
        />}
      </Group>}
      <TextButton
        kind={expanded ? 'secondary' : 'primary'}
        shape="round"
        size="medium"
        onClick={onTogglePackages}
        startIcon={expanded ? <LineMinusCircleIcon /> : <LinePlusCircleIcon />}
      >
        {expanded && intl.formatMessage({
          id: 'jeQJ9P1nHpCEAtSNDV46',
          defaultMessage: 'Hide packages',
          description: 'Button text for collapsing package options within a property room on the offer page.',
        })}
        {!expanded && intl.formatMessage(
          {
            id: 'RpgbUz_YnAMRJjdim6rP',
            defaultMessage: 'Show {packageCount, plural, one {# package} other {# packages}}',
            description: 'Button text for expanding package options within a property room on the offer page.',
          },
          { packageCount },
        )}
      </TextButton>
    </Group>
  </ActionsContainer>
}

export default PropertyCollapsedRoomPackageActions
